import { Link } from 'react-router-dom';
import type { ActivityFeedItem } from '../../lib/dashboardApi';
import { RelativeTime } from '../ui/RelativeTime';
import { TaskListRow } from '../tasks/TaskListRow';

interface ActivityFeedPanelProps {
  items: ActivityFeedItem[];
}

const TYPE_DOT: Record<ActivityFeedItem['type'], string> = {
  project: 'bg-blue-500',
  invoice: 'bg-emerald-500',
  task: 'bg-purple-500',
};

/** "created"/"updated" + who + when, as the line under a task row. */
function ActivityMeta({ item }: { item: ActivityFeedItem }) {
  return (
    <span className="text-[11px] text-gray-500">
      {item.action === 'created' ? 'Creat' : 'Actualizat'}
      {item.actor ? ` · ${item.actor}` : ''} · <RelativeTime iso={item.timestamp} />
    </span>
  );
}

/**
 * Recent activity feed with navigable links (CLE-256, Secțiunea 6).
 *
 * The feed mixes projects, invoices and tasks. Tasks are drawn with the shared
 * task row — the same one as every other task list — with the activity line
 * underneath; projects and invoices keep the dot + description line.
 */
export function ActivityFeedPanel({ items }: ActivityFeedPanelProps) {
  return (
    <section className="rounded-xl border border-border bg-card p-5 shadow-card">
      <h3 className="text-sm font-semibold text-foreground">Activitate recentă</h3>
      {items.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground/70">Nicio activitate recentă.</p>
      ) : (
        <ul className="mt-3 space-y-1.5">
          {items.map((item) => {
            if (item.type === 'task' && item.task) {
              return (
                <TaskListRow key={item.id} task={item.task}>
                  <ActivityMeta item={item} />
                </TaskListRow>
              );
            }
            const content = (
              <div className="flex gap-3">
                <span
                  className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${TYPE_DOT[item.type]}`}
                  aria-hidden
                />
                <div className="min-w-0">
                  <p className="text-sm text-foreground">{item.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {item.actor ? `${item.actor} · ` : ''}
                    <RelativeTime iso={item.timestamp} />
                  </p>
                </div>
              </div>
            );
            return (
              <li key={item.id}>
                {item.link ? (
                  <Link to={item.link} className="block rounded-md px-1 py-0.5 hover:bg-muted/40">
                    {content}
                  </Link>
                ) : (
                  <div className="px-1 py-0.5">{content}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
